/**
 * DecisionPointSummaryCard — Supporting Intelligence card for the right column.
 *
 * Per platform spec: DecisionPoint is supporting intelligence ONLY, never
 * the primary surface. This card is compact, secondary in visual weight,
 * and clearly labelled "Supporting Intelligence" so the user understands
 * its role next to the NTN Recommendation + Risk Indicators on the left.
 *
 * Shows: DecisionPoint Score, FICO Score, Eviction History, Criminal
 * History, Collections — read-only summary. The full editable DecisionPoint
 * report lives in the Documents/Activity section below.
 */
import { BarChart3, ShieldCheck, AlertTriangle, FileSpreadsheet } from "lucide-react";

const VERDICT_LABEL = {
  approve: "Recommended",
  conditional: "Recommended with Conditions",
  deny: "Not Recommended",
  APPROVE: "Recommended",
  CONDITIONAL: "Recommended with Conditions",
  DENY: "Not Recommended",
};

function StatusRow({ label, value, tone = "neutral", testid }) {
  const TONE = {
    good:    "text-emerald-700 bg-emerald-50 ring-emerald-200",
    warn:    "text-amber-700 bg-amber-50 ring-amber-200",
    bad:     "text-rose-700 bg-rose-50 ring-rose-200",
    neutral: "text-slate-700 bg-slate-50 ring-slate-200",
  };
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-b-0" data-testid={testid}>
      <span className="ntn-eyebrow">{label}</span>
      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ring-1 ring-inset ${TONE[tone]}`}>{value}</span>
    </div>
  );
}

export default function DecisionPointSummaryCard({ screening, isStaff = false, testid = "decisionpoint-summary-card" }) {
  const dp = screening.decisionpoint;
  const ordered = screening.effective_ordered_products
    || screening.ordered_products || screening.services || [];

  if (!dp || (dp.dp_score == null && dp.fico_score == null && !dp.fico_status && !dp.dp_score_status
              && dp.eviction_records_found == null && dp.criminal_records_found == null
              && !dp.final_verdict && !dp.suggested_verdict)) {
    return (
      <section className="bg-white rounded-lg ring-1 ring-slate-200 shadow-sm overflow-hidden" data-testid={testid}>
        <header className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
            <BarChart3 className="h-4 w-4 text-slate-600" />
          </div>
          <div>
            <div className="ntn-eyebrow">Supporting Intelligence</div>
            <div className="ntn-h3 mt-0.5">DecisionPoint Summary</div>
          </div>
        </header>
        <div className="ntn-secondary p-5">
          DecisionPoint report not yet on file. Available once NTN Staff posts results.
        </div>
      </section>
    );
  }

  const rawVerdict = (dp.final_verdict || dp.suggested_verdict || "").toLowerCase();
  const verdictLabel = VERDICT_LABEL[rawVerdict] || null;
  const verdictTone = rawVerdict === "approve" ? "good" : rawVerdict === "conditional" ? "warn" : rawVerdict === "deny" ? "bad" : "neutral";

  const dpScore = dp.dp_score;
  const fico = dp.fico_score;

  return (
    <section className="bg-white rounded-lg ring-1 ring-slate-200 shadow-sm overflow-hidden" data-testid={testid}>
      <header className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
            <BarChart3 className="h-4 w-4 text-slate-600" />
          </div>
          <div className="min-w-0">
            <div className="ntn-eyebrow">Supporting Intelligence</div>
            <div className="ntn-h3 mt-0.5">DecisionPoint Summary</div>
          </div>
        </div>
        {verdictLabel && (
          <span className={`ntn-eyebrow px-2 py-0.5 rounded-full ring-1 ring-inset ${
            verdictTone === "good" ? "text-emerald-700 bg-emerald-50 ring-emerald-200"
            : verdictTone === "warn" ? "text-amber-700 bg-amber-50 ring-amber-200"
            : verdictTone === "bad" ? "text-rose-700 bg-rose-50 ring-rose-200"
            : "text-slate-700 bg-slate-50 ring-slate-200"
          }`} data-testid={`${testid}-verdict`}>
            DP · {verdictLabel}
          </span>
        )}
      </header>

      {/* Score row — mini arc gauge (DP) + mini gradient bar (FICO) */}
      {(dpScore != null || fico != null || dp.fico_status || dp.dp_score_status) && (
        <div className="grid grid-cols-2 divide-x divide-slate-100 border-b border-slate-100">
          <div className="px-4 py-4" data-testid={`${testid}-dpscore`}>
            <div className="ntn-eyebrow text-center">DecisionPoint Score</div>
            <MiniScoreGauge value={dp.dp_score_status === "not_available" ? null : dpScore}
                            naLabel={dp.dp_score_status === "not_available" ? "N/A" : null} />
          </div>
          <div className="px-4 py-4 flex flex-col" data-testid={`${testid}-fico`}>
            <div className="ntn-eyebrow text-center">FICO Score</div>
            <MiniFicoBar value={fico} status={dp.fico_status} />
          </div>
        </div>
      )}

      {/* Status rows — eviction, criminal, collections.
          v1.0.6: never render "Clear"/"No records" for a category that was
          not ordered or whose result isn't in yet. Subscribers omit the row
          entirely when not ordered; staff see an explicit "Not Ordered". */}
      <div className="px-5 py-1">
        {(() => {
          const rows = [];
          const cat = (key, label, found, foundLabel, clearLabel, countHint) => {
            const isOrdered = ordered.includes(key);
            if (!isOrdered) {
              if (isStaff) rows.push(
                <StatusRow key={label} label={label} value="Not Ordered" tone="neutral"
                           testid={`${testid}-${label.toLowerCase().split(" ")[0]}`} />
              );
              return;
            }
            const tid = `${testid}-${label.toLowerCase().split(" ")[0]}`;
            if (found == null) {
              rows.push(<StatusRow key={label} label={label} value="Pending" tone="neutral" testid={tid} />);
            } else if (found) {
              rows.push(<StatusRow key={label} label={label} value={foundLabel} tone="bad" testid={tid} />);
            } else {
              rows.push(<StatusRow key={label} label={label} value={clearLabel} tone="good" testid={tid} />);
            }
          };
          cat("credit_report", "Eviction History", dp.eviction_records_found,
              `${dp.eviction_count || ""} record${dp.eviction_count === 1 ? "" : "s"} found`.trim(),
              "No records found");
          cat("criminal_report", "Criminal History", dp.criminal_records_found,
              "Records found", "Clear");
          return rows;
        })()}
        {(dp.collections != null || dp.collections_count != null) && (
          <StatusRow
            label="Collections"
            value={(dp.collections_count || dp.collections) ? `${dp.collections_count || dp.collections} item${(dp.collections_count || dp.collections) === 1 ? "" : "s"}` : "None"}
            tone={(dp.collections_count || dp.collections) ? "warn" : "good"}
            testid={`${testid}-collections`}
          />
        )}
      </div>

      <footer className="px-5 py-3 border-t border-slate-100 bg-slate-50/60">
        <p className="ntn-caption leading-snug flex items-start gap-1.5">
          <FileSpreadsheet className="h-3 w-3 mt-0.5 shrink-0" />
          Supporting intelligence. NTN&rsquo;s recommendation considers this alongside Risk Indicators and Verification Findings.
        </p>
      </footer>
    </section>
  );
}

/* ---------------- Mini DP arc gauge (compact, right-column) ---------------- */
function MiniScoreGauge({ value, max = 100, naLabel = null }) {
  const has = value != null;
  const pct = Math.max(0, Math.min(100, (Number(value || 0) / max) * 100));
  const radius = 52;
  const stroke = 9;
  const C = Math.PI * radius; // half-circumference
  const offset = C - (pct / 100) * C;
  const tone = pct >= 75 ? "#22C55E" : pct >= 50 ? "#F59E0B" : pct >= 1 ? "#EF4444" : "#94A3B8";
  const band = !has ? "Awaiting data" : pct >= 75 ? "Strong" : pct >= 50 ? "Moderate" : "Elevated";
  return (
    <div className="flex flex-col items-center mt-1" data-testid="dp-summary-mini-gauge">
      <svg viewBox="0 0 140 78" className="w-full max-w-[160px]">
        <path d={`M 8 70 A ${radius} ${radius} 0 0 1 132 70`}
          fill="none" stroke="#E2E8F0" strokeWidth={stroke} strokeLinecap="round" />
        {has && (
          <path d={`M 8 70 A ${radius} ${radius} 0 0 1 132 70`}
            fill="none"
            stroke={tone}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={C}
            strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 1s ease-out" }}
          />
        )}
        <text x="70" y="58" textAnchor="middle" fontFamily="Georgia, serif"
              fontSize="28" fill="#0F172A" fontWeight="600">
          {has ? Math.round(value) : (naLabel || "—")}
        </text>
        <text x="70" y="72" textAnchor="middle" fontFamily="Helvetica, sans-serif"
              fontSize="7" letterSpacing="2" fill="#64748B" fontWeight="700">
          / {max}
        </text>
      </svg>
      <div className="text-[9px] font-mono tracking-[0.14em] mt-0.5" style={{ color: tone }}>
        {band.toUpperCase()}
      </div>
    </div>
  );
}

/* ---------------- Mini FICO gradient bar with pointer ---------------- */
function MiniFicoBar({ value, status }) {
  // v1.0.9 — three states: numeric score / -9999 insufficient history / N/A.
  const na = status === "not_available";
  const insuff = status === "insufficient_history" || value === -9999;
  const has = !na && !insuff && value != null;
  // FICO range 300-850
  const pct = has ? Math.max(0, Math.min(100, ((Number(value) - 300) / 550) * 100)) : 0;
  const tier = na ? "N/A"
    : insuff ? "Insufficient history"
    : !has ? "Awaiting"
    : value >= 800 ? "Exceptional"
    : value >= 740 ? "Very Good"
    : value >= 670 ? "Good"
    : value >= 580 ? "Fair"
    : "Poor";
  const tone = !has ? "#64748B"
    : value >= 740 ? "#22C55E"
    : value >= 670 ? "#84CC16"
    : value >= 580 ? "#F59E0B"
    : "#EF4444";
  return (
    <div className="mt-2 flex-1 flex flex-col justify-center" data-testid="dp-summary-mini-fico">
      <div className="flex items-baseline justify-center gap-2">
        <div className="font-serif text-3xl text-slate-900 tabular-nums leading-none">
          {has ? value : na ? "N/A" : insuff ? "-9999" : "—"}
        </div>
        <div className="text-[9px] font-mono tracking-[0.14em]" style={{ color: tone }}>
          {tier.toUpperCase()}
        </div>
      </div>
      <div className="mt-4">
        <div className="relative h-1.5 rounded-full bg-gradient-to-r from-[#EF4444] via-[#F59E0B] to-[#22C55E]">
          {has && (
            <div className="absolute -top-1 h-3.5 w-0.5 bg-slate-900 rounded-sm shadow-sm"
                 style={{ left: `calc(${pct}% - 1px)`, transition: "left 1s ease-out" }} />
          )}
        </div>
        <div className="flex justify-between text-[7px] font-mono tracking-[0.1em] text-slate-500 mt-1.5">
          <span>300</span><span>850</span>
        </div>
      </div>
    </div>
  );
}
